import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useVault } from "@/context/VaultContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PasswordField } from "./PasswordField";
import { StrengthMeter } from "./StrengthMeter";
import { RecoveryCodeCard } from "./RecoveryCodeCard";
import { passwordStrength } from "@/lib/vaultCrypto";
import { errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, LifeBuoy, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Vises rett etter at hvelvet er låst opp med GJENOPPRETTINGSKODE: brukeren velger et nytt hovedpassord
 * (hvelvnøkkelen pakkes inn på nytt – innholdet krypteres ikke om) og får en ny gjenopprettingskode.
 * Montert én gang i AppShell slik at den følger brukeren uansett hvilken Passord-side som låste opp.
 */
export const RecoveryFollowUpDialog = () => {
  const { user } = useAuth();
  const { status, recoveryFollowUp, setMasterAfterRecovery, dismissRecoveryFollowUp } = useVault();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [code, setCode] = useState(null);
  const open = recoveryFollowUp && status === "unlocked";
  const strength = passwordStrength(pw);
  const okStrength = pw.length >= 12 && strength.score >= 2;
  const match = pw && pw === pw2;

  useEffect(() => { if (!open) { setPw(""); setPw2(""); setError(""); setCode(null); } }, [open]);

  const later = () => {
    dismissRecoveryFollowUp();
    toast("Du kan bytte hovedpassord når som helst under Hvelvinnstillinger.", { id: "vault-recovery-later" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!okStrength) { setError("Hovedpassordet må være minst 12 tegn og av middels styrke eller bedre."); return; }
    if (!match) { setError("Passordene er ikke like."); return; }
    setBusy(true);
    try {
      const newCode = await setMasterAfterRecovery(pw);
      setPw(""); setPw2("");
      setCode(newCode);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const finish = () => {
    dismissRecoveryFollowUp();
    toast.success("Nytt hovedpassord og ny gjenopprettingskode er aktive.", { id: "vault-recovery-done" });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy && !code) later(); }}>
      <DialogContent className={cn("bg-card sm:max-w-lg", code && "[&>button:last-child]:hidden")} data-testid="recovery-followup-dialog" onInteractOutside={(e) => { if (code) e.preventDefault(); }} onEscapeKeyDown={(e) => { if (code) e.preventDefault(); }}>
        {code ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><LifeBuoy className="h-5 w-5 text-primary" /> Lagre den nye gjenopprettingskoden</DialogTitle>
              <DialogDescription>Den gamle koden virker ikke lenger. Dette er eneste gang den nye vises – lagre den adskilt fra hovedpassordet.</DialogDescription>
            </DialogHeader>
            <RecoveryCodeCard code={code} userEmail={user?.email} confirmLabel="Jeg har lagret den nye koden" onConfirmed={finish} />
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" /> Velg nytt hovedpassord</DialogTitle>
              <DialogDescription>Du låste opp med gjenopprettingskoden. Velg et nytt hovedpassord nå, så kan du låse opp som vanlig neste gang. Innholdet i hvelvet beholdes.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" noValidate>
              <Alert data-testid="recovery-followup-note">
                <LifeBuoy className="h-4 w-4" />
                <AlertTitle>Du får også en ny gjenopprettingskode</AlertTitle>
                <AlertDescription>Når det nye hovedpassordet er lagret, lages en ny kode. Den gamle slutter da å virke.</AlertDescription>
              </Alert>
              <div className="space-y-1.5">
                <Label htmlFor="rf-master">Nytt hovedpassord</Label>
                <PasswordField id="rf-master" value={pw} onChange={(e) => setPw(e.target.value)} testId="recovery-followup-master" sensitive={false} autoComplete="new-password" />
                <StrengthMeter password={pw} testId="recovery-followup-strength" />
                <p className="text-xs text-muted-foreground">Gjerne en setning på 4–5 ord du husker. Minst 12 tegn.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rf-master2">Gjenta nytt hovedpassord</Label>
                <PasswordField id="rf-master2" value={pw2} onChange={(e) => setPw2(e.target.value)} testId="recovery-followup-master-repeat" sensitive={false} autoComplete="new-password" />
                {pw2 && !match ? <p className="text-xs text-destructive" data-testid="recovery-followup-mismatch">Passordene er ikke like.</p> : null}
              </div>
              {error ? <p className="text-sm text-destructive" role="alert" data-testid="recovery-followup-error">{error}</p> : null}
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={later} disabled={busy} data-testid="recovery-followup-later">Senere</Button>
                <Button type="submit" disabled={busy || !okStrength || !match} data-testid="recovery-followup-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />} {busy ? "Lagrer …" : "Lagre nytt hovedpassord"}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
